import type { SectionProps } from "../context";
import { field } from "../context";
import { formatRupiah } from "../helpers";
import { Icon } from "../icons";
import { BuyLink, CartAdd, KindChip, StorePrice, cheapest, productHref, type StoreProductCard } from "../store";
import { Container, Eyebrow, Img, MultiLine, OrderLink, REVEAL, STAGGER, SectionShell, SectionTitle, SiteLink, cx } from "../ui";

type Props = SectionProps<"product-feature">;

/**
 * One product sold from the page: the owner's headline and pitch, the product's cover, price and
 * highlights from the Toko, and one clear buy button. Without a matching store product it shows the
 * section's own price and image and orders through WhatsApp.
 */
export function ProductFeature({ node, ctx }: Props) {
  const c = node.content;
  const product = (c.productSlug ? ctx.products?.find((p) => p.slug === c.productSlug) : ctx.products?.[0]) ?? null;
  const center = node.variant === "stack";
  const split = node.variant === "split";
  const cover = product?.cover ? { src: product.cover, alt: product.title } : c.image;
  const book = product ? product.kind === "ebook" : true;

  const media = (
    <div className={cx("baze-reveal-pop relative mx-auto w-full", book ? "max-w-[22rem]" : "max-w-xl")}>
      {node.variant === "spotlight" ? <span aria-hidden="true" className="absolute -inset-10 -z-10 rounded-full bg-site-primary/15 blur-3xl" /> : null}
      <div
        className={cx(
          "relative overflow-hidden bg-site-surface",
          book ? "aspect-[3/4] rounded-r-site-md rounded-l-[4px]" : "aspect-video rounded-site-lg",
          node.variant === "spotlight" && book ? "shadow-[18px_24px_50px_-18px_rgb(0_0_0/0.45)] [transform:perspective(1400px)_rotateY(-14deg)]" : "shadow-site-md",
        )}
      >
        {cover ? (
          <Img ctx={ctx} image={cover} sizes="(min-width: 1024px) 420px, 80vw" path={product ? undefined : "content.image"} target={900} />
        ) : (
          <div aria-hidden="true" className="baze-heading grid h-full w-full place-items-center bg-site-primary/10 p-6 text-center text-2xl text-site-primary">
            {product?.title ?? c.title}
          </div>
        )}
        {/* Spine and page edge: the cover reads as a book, not a flat picture. */}
        {book ? <span aria-hidden="true" className="absolute inset-y-0 left-0 w-3 bg-gradient-to-r from-black/25 via-white/10 to-transparent" /> : null}
      </div>
    </div>
  );

  const bullets = c.bullets?.length ? c.bullets : (product?.highlights ?? []);
  const priceBlock = product ? (
    <div className="mt-8">
      {c.showTiers && product.tiers.length > 1 ? (
        <Tiers ctx={ctx} product={product} center={center} />
      ) : (
        <div className={cx("flex flex-wrap items-center gap-x-5 gap-y-4", center && "justify-center")}>
          <StorePrice product={product} size="lg" className="text-site-text" />
          {ctx.store ? (
            <span className="flex flex-wrap items-center gap-2">
              <BuyLink ctx={ctx} product={product} label={c.ctaLabel || (cheapest(product).price > 0 ? `Beli ${formatRupiah(cheapest(product).price)}` : "Beli sekarang")} className="min-h-12 px-7 text-base" />
              <CartAdd ctx={ctx} product={product} />
            </span>
          ) : (
            <OrderLink ctx={ctx} produk={product.title} label={c.ctaLabel || "Pesan"} className="inline-flex min-h-12 items-center gap-2 rounded-site-button bg-site-primary px-7 text-base font-semibold text-site-on-primary shadow-site-sm" />
          )}
        </div>
      )}
      {ctx.store ? (
        <p className={cx("mt-3 flex items-center gap-1.5 text-xs text-site-muted", center && "justify-center")}>
          <Icon name="lock" className="size-3.5" />
          Bayar aman · file langsung bisa diunduh
        </p>
      ) : null}
    </div>
  ) : (
    <div className={cx("mt-8 flex flex-wrap items-center gap-x-5 gap-y-4", center && "justify-center")}>
      {c.price !== undefined ? (
        <span className="baze-heading text-3xl text-site-text tabular-nums @3xl:text-4xl" {...field(ctx, "content.price")}>
          {formatRupiah(c.price)}
        </span>
      ) : null}
      <OrderLink ctx={ctx} produk={c.title} label={c.ctaLabel || "Pesan sekarang"} className="inline-flex min-h-12 items-center gap-2 rounded-site-button bg-site-primary px-7 text-base font-semibold text-site-on-primary shadow-site-sm" />
    </div>
  );

  const copy = (
    <div className={cx(REVEAL, center && "mx-auto max-w-2xl text-center")}>
      <div className={cx("mb-4 flex flex-wrap items-center gap-2", center && "justify-center")}>
        {product ? <KindChip kind={product.kind} /> : null}
        <Eyebrow ctx={ctx} text={c.eyebrow} path="content.eyebrow" />
      </div>
      <SectionTitle ctx={ctx} node={node} text={c.title} className="text-balance" />
      {product && ctx.store ? (
        <p className="mt-3 text-sm text-site-muted">
          <SiteLink ctx={ctx} href={productHref(product.slug)} className="font-semibold text-site-text underline decoration-site-border underline-offset-4 hover:decoration-site-primary">
            {product.title}
          </SiteLink>
          {product.subtitle ? ` — ${product.subtitle}` : null}
        </p>
      ) : null}
      <MultiLine ctx={ctx} text={c.description} path="content.description" className="mt-5 text-base leading-relaxed text-site-muted @3xl:text-lg" />
      {bullets.length ? (
        <ul className={cx(STAGGER, "mt-7 grid gap-3 text-left", center && "mx-auto max-w-md")}>
          {bullets.map((b, i) => (
            <li key={i} className="flex gap-3 text-[0.9375rem] leading-relaxed text-site-text" {...(c.bullets?.length ? field(ctx, `content.bullets.${i}`) : {})}>
              <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-site-primary text-site-on-primary">
                <Icon name="check" className="size-3" />
              </span>
              {b}
            </li>
          ))}
        </ul>
      ) : null}
      {priceBlock}
      {c.guarantee ? (
        <p className={cx("mt-6 inline-flex items-center gap-2 rounded-full bg-site-surface px-4 py-2 text-sm text-site-text ring-1 ring-site-border")} {...field(ctx, "content.guarantee")}>
          <Icon name="star" className="size-4 text-site-primary" />
          {c.guarantee}
        </p>
      ) : null}
    </div>
  );

  if (center) {
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container>
          <div className="mx-auto mb-12 max-w-sm">{media}</div>
          {copy}
        </Container>
      </SectionShell>
    );
  }
  return (
    <SectionShell ctx={ctx} node={node}>
      <Container>
        <div className="grid items-center gap-12 @4xl:grid-cols-2 @4xl:gap-16">
          <div className={cx(split && "@4xl:order-2")}>{media}</div>
          {copy}
        </div>
      </Container>
    </SectionShell>
  );
}

/** Tier cards ("Basic" / "Pro"), each with its own price and buy link. */
function Tiers({ ctx, product, center }: { ctx: Props["ctx"]; product: StoreProductCard; center: boolean }) {
  return (
    <ul className={cx("grid gap-3 @xl:grid-cols-2", center && "mx-auto max-w-xl text-left")}>
      {product.tiers.map((t, i) => (
        <li key={t.id} className={cx("flex flex-col gap-3 rounded-site-md border bg-site-surface p-5", i === product.tiers.length - 1 ? "border-site-primary ring-1 ring-site-primary" : "border-site-border")}>
          <p className="text-sm font-semibold text-site-text">{t.name}</p>
          <p className="flex items-baseline gap-2">
            <span className="text-2xl font-semibold text-site-text tabular-nums">{formatRupiah(t.price)}</span>
            {t.compareAt && t.compareAt > t.price ? <s className="text-sm text-site-muted tabular-nums">{formatRupiah(t.compareAt)}</s> : null}
          </p>
          {ctx.store ? (
            <span className="flex flex-col gap-2">
              <BuyLink ctx={ctx} product={product} tier={t.id} label={`Pilih ${t.name}`} className="w-full" />
              <CartAdd ctx={ctx} product={product} tier={t.id} className="w-full" />
            </span>
          ) : (
            <OrderLink ctx={ctx} produk={`${product.title} (${t.name})`} className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-site-button border border-site-border text-sm font-semibold text-site-text" />
          )}
        </li>
      ))}
    </ul>
  );
}
